import React, { useEffect } from 'react';
import { useAppSelector } from 'hooks';
import { Input, Label } from 'reactstrap';
import dayjs from 'dayjs';
import { defaultMinuteInterval, formatDateTimeValue, getTime, timeValueFormat } from 'tsx/libs/dayjs';

import ClickableIcon from '~components/ClickableIcon';
import { InputProps } from '~components/form-fields';
import * as FormInput from '~components/form-fields/inputs';
import { faAngleLeft, faAngleRight } from '~components/icon';

import { derivePaymentAmount } from '~appointments/lib/derive';
import { selectCompanySettings } from '~main/selectors/login';

function FormInputStartEndTimes({ value, onChange, disabled, setFieldValue }: InputProps) {
  let { start_time, end_time, ends_next_day } = value;
  const { service_type_id } = value;

  let minutes = defaultMinuteInterval;
  const settings = useAppSelector(selectCompanySettings);
  if (settings?.SYSTEM_2022_IntervalHops) minutes = parseInt(settings.SYSTEM_2022_IntervalHops as string);

  start_time = formatDateTimeValue('time', start_time);
  end_time = formatDateTimeValue('time', end_time);
  ends_next_day = formatDateTimeValue('checkbox', ends_next_day);

  useEffect(() => {
    if (start_time && !end_time) {
      const newEndTime = getTime(start_time, minutes);
      onChange && onChange('end_time', dayjs(newEndTime).format(timeValueFormat));
    }
  }, [start_time, end_time, minutes]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    onChange && onChange(name, checked);
  };

  const isQuickMoveDisabled = (!start_time && !end_time) || disabled;

  const handleQuickTimeShift = async (addTime: boolean) => {
    if (isQuickMoveDisabled) return;

    const timeShift = addTime ? minutes : -minutes;
    const newStartTime = getTime(start_time, timeShift);
    const newEndTime = getTime(end_time, timeShift);
    const formattedStartTime = dayjs(newStartTime).format(timeValueFormat);
    const formattedEndTime = dayjs(newEndTime).format(timeValueFormat);

    // The shift can move the pair across (or out of) midnight independently of the pre-shift
    // ends_next_day flag (e.g. 23:45-00:45 shifted +15min becomes 00:00-01:00, no longer
    // overnight). Derive it fresh from the shifted HH:mm pair rather than trusting the stale
    // flag, or deriveDuration() can add a spurious extra day (TAV-2992).
    // A 24hr/sleepover appointment (equal start/end with ends_next_day already true) shifts to
    // another equal pair, which isBefore() alone reads as same-day (0 minutes) rather than the
    // 1440-minute overnight span deriveDuration() gives it, collapsing duration and payment.
    // Keep the overnight flag when the shifted times are still equal and it was already set (TAV-2992).
    const shiftedEndsNextDay =
      dayjs(formattedEndTime, timeValueFormat).isBefore(dayjs(formattedStartTime, timeValueFormat)) ||
      (dayjs(formattedEndTime, timeValueFormat).isSame(dayjs(formattedStartTime, timeValueFormat)) && ends_next_day);

    // onChange (Fields.tsx's renderField closure) is async and only applies its derived
    // payment_amount write after an await, on a stale values snapshot from before this click
    // (TAV-2992). Awaiting both calls here means their buggy payment_amount write has already
    // landed before we overwrite it below — otherwise it lands after ours and clobbers it.
    if (onChange) {
      await onChange('start_time', formattedStartTime);
      await onChange('end_time', formattedEndTime);
      // Only write ends_next_day when the shift actually changes it: the common case leaves it
      // untouched, and an unconditional write here would re-trigger setFieldTouched/onChange for
      // a field that never needed to change, and can add it to a diff-based submit payload that
      // never carried it before (TAV-2992).
      if (shiftedEndsNextDay !== ends_next_day) {
        await onChange('ends_next_day', shiftedEndsNextDay);
      }
    }

    const correctedPaymentAmount = derivePaymentAmount({
      start_time: formattedStartTime,
      end_time: formattedEndTime,
      ends_next_day: shiftedEndsNextDay,
      service_type_id,
    });
    if (correctedPaymentAmount !== undefined) {
      setFieldValue && setFieldValue('payment_amount', correctedPaymentAmount);
    }
  };

  return (
    <div className="d-flex flex-wrap align-items-center">
      <div className="d-flex align-items-center me-2 mb-2">
        <FormInput.Time
          id="start_time"
          name="start_time"
          type="time"
          value={start_time ?? ''}
          disabled={disabled}
          placeholder="Start"
          onChange={onChange}
          dataCy="start_time"
        />
        <div className="ms-2 me-2">to</div>
        <FormInput.Time
          id="end_time"
          name="end_time"
          type="time"
          value={end_time ?? ''}
          disabled={disabled}
          placeholder="End"
          onChange={onChange}
          dataCy="end_time"
        />
        <ClickableIcon
          id="move_time_earlier_icon"
          icon={faAngleLeft}
          className="ms-2"
          disabled={isQuickMoveDisabled}
          onClick={() => handleQuickTimeShift(false)}
          dataCy="move-time-earlier-icon"
          toolTip="Move time earlier"
          ariaLabel="Move time earlier"
        />
        <ClickableIcon
          id="move_time_later_icon"
          icon={faAngleRight}
          className="me-2"
          disabled={isQuickMoveDisabled}
          onClick={() => handleQuickTimeShift(true)}
          dataCy="move-time-later-icon"
          toolTip="Move time later"
          ariaLabel="Move time later"
        />
      </div>
      <div className="d-flex align-items-center justify-content-center w-auto mb-2">
        <Input
          id="ends_next_day"
          type="checkbox"
          name="ends_next_day"
          checked={ends_next_day ?? false}
          onChange={handleCheckboxChange}
          data-cy="ends_next_day-input"
          className="mb-1"
        />
        <Label for="ends_next_day" className="ms-2 mb-0">
          Overnight
        </Label>
      </div>
    </div>
  );
}

export default FormInputStartEndTimes;
